// Writing screen (the `writing` free-response step). Twin of the desktop
// WritingPage. Full-bleed white: optional centered title, a Markdown prompt in
// a light-grey fixed-height box, the writing textarea, and a footer with the
// countdown (left), the counter (right), and the Submit button (far right).
//
// Parity with writing_page.py / writing_page_style.qss:
//   - count_by "words" | "characters"; word count splits on whitespace.
//   - Counter shows only when a min or max is set; text formats match desktop
//     ("47 / 100 words", "... minimum", "... maximum").
//   - Submit is disabled until min_count is met (max is shown, NOT enforced).
//   - end_condition "submit" | "timed" | "both": the timer runs for timed/both
//     and auto-submits at duration_seconds (end_trigger "timed_auto"); the
//     Submit button is shown in every mode (matches desktop), so "timed" and
//     "both" behave the same. Optional "M:SS remaining" countdown.
//   - pause_after_seconds keeps the page up before advancing.
//   - Submit payload: { response_text, word_count, character_count,
//     time_on_block_seconds, end_trigger } (both counts always sent).

import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { WebRendererProps } from "./registry";
import type { DocMidBlockSurvey, VideoSurveyResponse } from "../api/types";
import { fetchWriting } from "../session/sessionDriver";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

export default function WritingRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;

  const title = typeof settings.title === "string" ? settings.title.trim() : "";
  const promptText = typeof settings.prompt_text === "string" ? settings.prompt_text : "";
  const placeholder =
    typeof settings.placeholder === "string" && settings.placeholder
      ? settings.placeholder
      : "Start writing here...";
  const countBy = settings.count_by === "characters" ? "characters" : "words";
  const minCount = typeof settings.min_count === "number" ? settings.min_count : null;
  const maxCount = typeof settings.max_count === "number" ? settings.max_count : null;
  const endCondition = (settings.end_condition as string) ?? "submit";
  const durationSeconds =
    typeof settings.duration_seconds === "number" ? settings.duration_seconds : 300;
  const showCountdown =
    Boolean(settings.show_countdown) && (endCondition === "timed" || endCondition === "both");
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const [text, setText] = useState("");
  const [remaining, setRemaining] = useState(durationSeconds);
  const [submitted, setSubmitted] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState<DocMidBlockSurvey | null>(null);
  const endedRef = useRef(false);
  const startedAt = useRef<number>(performance.now());
  // Mid-block survey state (mirrors DocumentRenderer/VideoRenderer): fired-once
  // tracking, the in-flight survey that gates + defers any block-end, and the
  // captured responses bundled into the result.
  const triggersRef = useRef<DocMidBlockSurvey[]>([]);
  const firedRef = useRef<Set<number>>(new Set());
  const awaitingSurveyRef = useRef(false);
  const pendingEndRef = useRef<"submit" | "timed_auto" | null>(null);
  const surveyResponsesRef = useRef<VideoSurveyResponse[]>([]);
  const activeSurveyRef = useRef<DocMidBlockSurvey | null>(null);
  const surveyTimersRef = useRef<number[]>([]);

  // [...text].length counts Unicode code points (matches Python len()); text.length
  // would count UTF-16 units, over-counting emoji/astral characters vs in-lab.
  const count = countBy === "characters" ? [...text].length : wordCount(text);
  const unit = countBy === "characters" ? "characters" : "words";
  const minMet = minCount === null || count >= minCount;

  function finish(endTrigger: "submit" | "timed_auto") {
    // Never end out from under an open mid-block survey; honor it on close.
    if (awaitingSurveyRef.current) {
      pendingEndRef.current = endTrigger;
      return;
    }
    if (endedRef.current) return;
    endedRef.current = true;
    setSubmitted(true);
    surveyTimersRef.current.forEach((t) => window.clearInterval(t));
    const elapsed = (performance.now() - startedAt.current) / 1000;
    const result = {
      response_text: text,
      word_count: wordCount(text),
      character_count: [...text].length,
      time_on_block_seconds: Math.round(elapsed * 1000) / 1000,
      end_trigger: endTrigger,
      survey_responses: surveyResponsesRef.current,
    };
    const submit = () => onDone({ kind: "submit", result });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  // Fire the earliest not-yet-fired time trigger the elapsed clock has reached,
  // one at a time. The survey overlays the writing block; the block-end (if any)
  // is deferred until it closes. Mirrors DocumentRenderer's mid-block logic.
  function checkSurveyTriggers(elapsedSeconds: number) {
    if (awaitingSurveyRef.current || endedRef.current) return;
    const list = triggersRef.current;
    for (let i = 0; i < list.length; i++) {
      if (firedRef.current.has(i)) continue;
      if (elapsedSeconds >= list[i].value) {
        firedRef.current.add(i);
        fireSurvey(list[i]);
        return;
      }
      break; // value-sorted: the first unreached trigger ends the scan
    }
  }

  function fireSurvey(trigger: DocMidBlockSurvey) {
    awaitingSurveyRef.current = true;
    activeSurveyRef.current = trigger;
    setActiveSurvey(trigger);
  }

  function onSurveySubmit(responses: SurveyResponse[]) {
    const trigger = activeSurveyRef.current;
    if (trigger) {
      surveyResponsesRef.current.push({
        survey_id: trigger.survey_id,
        survey_name: trigger.survey_name,
        responses,
      });
    }
    activeSurveyRef.current = null;
    setActiveSurvey(null);
    awaitingSurveyRef.current = false;
    // Honor a block-end that came due while the survey was open.
    if (pendingEndRef.current) {
      const t = pendingEndRef.current;
      pendingEndRef.current = null;
      finish(t);
    }
  }

  // Timed end + optional countdown (timed / both).
  useEffect(() => {
    if (endCondition !== "timed" && endCondition !== "both") return;
    const endId = window.setTimeout(() => finish("timed_auto"), durationSeconds * 1000);
    let cdId: number | undefined;
    if (showCountdown) {
      cdId = window.setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    }
    return () => {
      clearTimeout(endId);
      if (cdId) clearInterval(cdId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arm mid-block surveys: fetch the resolved triggers (with inline questions)
  // and poll them against the elapsed clock. Non-blocking: the writing UI is
  // already rendered; the surveys just arm once the fetch returns.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const content = await fetchWriting(session);
        if (!alive) return;
        triggersRef.current = [...(content.mid_block_surveys ?? [])].sort(
          (a, b) => a.value - b.value,
        );
        if (triggersRef.current.length > 0) {
          const iv = window.setInterval(() => {
            checkSurveyTriggers((performance.now() - startedAt.current) / 1000);
          }, 500);
          surveyTimersRef.current.push(iv);
        }
      } catch {
        // A survey-fetch failure must never trap the participant: the writing
        // block still runs and ends normally, just without mid-block surveys.
      }
    })();
    return () => {
      alive = false;
      surveyTimersRef.current.forEach((t) => window.clearInterval(t));
      surveyTimersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Counter text (only shown when a constraint exists).
  let counterText = "";
  if (minCount !== null && maxCount !== null) counterText = `${count} / ${maxCount} ${unit}`;
  else if (minCount !== null) counterText = `${count} / ${minCount} ${unit} minimum`;
  else if (maxCount !== null) counterText = `${count} / ${maxCount} ${unit} maximum`;
  const showCounter = minCount !== null || maxCount !== null;

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const countdownText = `${minutes}:${seconds.toString().padStart(2, "0")} remaining`;

  return (
    <div className="jol-writing">
      {title ? <div className="jol-writing-title">{title}</div> : null}

      {promptText ? (
        <div className="jol-writing-prompt">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{promptText}</ReactMarkdown>
        </div>
      ) : null}

      <textarea
        className="jol-writing-area"
        value={text}
        placeholder={placeholder}
        autoFocus
        disabled={submitted}
        onChange={(e) => setText(e.target.value)}
      />

      <div className="jol-writing-footer">
        {showCountdown ? <span className="jol-writing-countdown">{countdownText}</span> : null}
        {showCounter ? <span className="jol-writing-counter">{counterText}</span> : null}
        <button
          type="button"
          className="jol-writing-submit"
          disabled={!minMet || submitted}
          onClick={() => finish("submit")}
        >
          {submitted ? "Submitted" : "Submit"}
        </button>
      </div>

      {activeSurvey ? (
        <div className="jol-writing-survey-overlay">
          <SurveyForm
            questions={activeSurvey.questions}
            submitLabel="Submit"
            onSubmit={onSurveySubmit}
          />
        </div>
      ) : null}
    </div>
  );
}
